import React, { useEffect, FC, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "react-responsive";
import { useDebounce } from "use-debounce";
import { useQueryParams } from "src/hooks/useQueryParams";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import { selectInquiries } from "src/redux/inquiries/selectors";
import { getInquiriesAsync, ITEMS_PER_PAGE } from "src/redux/inquiries/actions";
import { selectFiltersValues } from "src/redux/filters/selectors";
import { getFiltersValuesAsync } from "src/redux/filters/actions";
import { InquirySuggestionBanner } from "src/page-components/clones/InquirySuggestionBanner";
import {
  inquiriesTrns,
  noInquiriesBannerTrns,
} from "src/page-components/inquiries/trns";
import { Link } from "src/components/Link";
import { Filtering } from "src/components/Filtering";
import { InquiryCard } from "src/components/InquiryCard";
import { PageWrapper } from "src/components/Layouts/PageWrapper";
import { SortingDropdownList } from "src/components/SortingDropdownList";
import { INQUIRIES_SORTING_OPTIONS } from "src/components/SortingDropdownList/constants";
import { Loader } from "src/components/Loader";
import { Pagination } from "src/components/Pagination";
import { Button } from "src/components/Button";
import { ButtonVariants } from "src/components/Button/types";
import { NoItemsBanner } from "src/components/NoItemsBanner";
import { FilteringButton } from "src/components/Filtering/FilteringButton";
import { MobFilteringMenu } from "src/components/Filtering/MobFilteringMenu";
import { Show } from "src/components/PrivateRoute/Show";
import { ONLY_FOR } from "src/components/PrivateRoute/types";
import { BreadCrumbs } from "src/components/BreadCrumbs";
import { breadCrumbsTrns } from "src/components/BreadCrumbs/trns";
import { MetadataWrapper } from "src/components/MetadataWrapper";
import { IBreadCrumbsItem } from "src/components/BreadCrumbs/types";
import { scrollTop } from "src/utils/scrollTop";
import { PATHNAMES } from "src/constants/routes";
import { QUERY_PARAM_KEYS } from "src/constants/queryParams";
import { SCREEN_BREAKPOINTS } from "src/constants/screenBreakpoints";
import { ISelectOption } from "src/@types/form";
import { Sizes } from "src/@types/sizes";
import { ReactComponent as PlusIcon } from "src/assets/icons/plus.svg";

const DEBOUNCE_DELAY_DESKTOP = 1000;
const DEBOUNCE_DELAY_MOBILE = 2500;

const Inquiries: FC = () => {
  const { t } = useTranslation();

  const [isOpenFiltersMenu, setIsOpenFiltersMenu] = useState(false);
  const isGLScreenBreakpoint = useMediaQuery({
    maxWidth: SCREEN_BREAKPOINTS.GL,
  });

  const debounceDelay = isGLScreenBreakpoint
    ? DEBOUNCE_DELAY_MOBILE
    : DEBOUNCE_DELAY_DESKTOP;

  const dispatch = useAppDispatch();

  const { queryParams, getQueryParam, setQueryParam, setMultipleQueryParams } =
    useQueryParams();

  const [debouncedQueryParams] = useDebounce(queryParams, debounceDelay);

  const filtersValues = useAppSelector(selectFiltersValues);

  useEffect(() => {
    dispatch(getFiltersValuesAsync());
  }, [dispatch]);

  const activeOption = INQUIRIES_SORTING_OPTIONS.find(
    ({ value }) => value === getQueryParam(QUERY_PARAM_KEYS.SORTING)
  );

  const setOption = ({ value }: ISelectOption) =>
    setMultipleQueryParams({
      [QUERY_PARAM_KEYS.SORTING]: value,
      [QUERY_PARAM_KEYS.PAGE]: 1,
    });

  const currentPage = Number(getQueryParam(QUERY_PARAM_KEYS.PAGE)) || 1;

  const setPage = (page: number) => {
    scrollTop();
    if (page !== currentPage) {
      setQueryParam(QUERY_PARAM_KEYS.PAGE, page.toString());
    }
  };

  const filteredQueryParams = useMemo(() => {
    const params = new URLSearchParams(debouncedQueryParams);
    params.delete(QUERY_PARAM_KEYS.IS_SHOWED_MORE);
    return params.toString();
  }, [debouncedQueryParams]);

  useEffect(() => {
    dispatch(getInquiriesAsync(filteredQueryParams));
  }, [filteredQueryParams]);

  const inquiriesResponse = useAppSelector(selectInquiries);
  const { isLoading, data } = inquiriesResponse || {};

  const inquiriesResult = data?.results || [];

  const pageCount = Math.ceil(data?.count / ITEMS_PER_PAGE);
  const isPaginated = pageCount > 1;

  const breadCrumbsItems: IBreadCrumbsItem[] = [
    { title: { i18nKey: breadCrumbsTrns.home }, path: PATHNAMES.HOME },
    { title: { i18nKey: breadCrumbsTrns.inquiries } },
  ];

  return (
    <MetadataWrapper name="inquiries">
      <PageWrapper mainClassName="pt-10 pb-25 mt-19">
        <section className="container flex flex-col gap-3 gl:gap-10">
          {!isGLScreenBreakpoint && <BreadCrumbs items={breadCrumbsItems} />}

          <h1 className="font-semibold">{t(inquiriesTrns.title)}</h1>

          <div className="flex items-start justify-between gap-3">
            {!isGLScreenBreakpoint && (
              <Filtering
                filtersValues={filtersValues?.inquiry}
                pagePath={PATHNAMES.INQUIRIES}
              />
            )}

            {/* Inquiries sort and buttons */}

            {isLoading ? (
              <Loader size={Sizes.L} className="self-center" />
            ) : (
              <div className="flex flex-col gap-3 w-full">
                {isGLScreenBreakpoint && (
                  <>
                    <div className="relative">
                      <FilteringButton
                        setIsOpen={setIsOpenFiltersMenu}
                        // className="sm:absolute top-3 left-0"
                      />
                    </div>
                    <MobFilteringMenu
                      isOpen={isOpenFiltersMenu}
                      setIsOpen={setIsOpenFiltersMenu}
                      filtersValues={filtersValues?.inquiry}
                      pagePath={PATHNAMES.INQUIRIES}
                    />
                  </>
                )}

                <div className="flex flex-wrap items-center justify-between gap-3">
                  <SortingDropdownList
                    options={INQUIRIES_SORTING_OPTIONS}
                    activeOption={activeOption || INQUIRIES_SORTING_OPTIONS[0]}
                    setOption={setOption}
                  />

                  <Show onlyFor={ONLY_FOR.AGENT} showLoader={false}>
                    <Link href={PATHNAMES.CREATE_INQUIRY}>
                      <Button
                        variant={ButtonVariants.SECONDARY}
                        className="flex items-center justify-center lg:px-5 py-2"
                      >
                        <PlusIcon />
                        <span>{t(inquiriesTrns.addInquiryButton)}</span>
                      </Button>
                    </Link>
                  </Show>
                </div>

                {/* Inquiries list */}
                {data?.count > 0 ? (
                  <div className="flex flex-col gap-3">
                    {Boolean(inquiriesResult?.length) &&
                      inquiriesResult.map((inquiry) => (
                        <InquiryCard
                          key={inquiry.id}
                          inquiry={inquiry}
                          isReadOnly
                        />
                      ))}

                    {isPaginated && (
                      <Pagination
                        className="my-8 mx-auto"
                        page={currentPage}
                        pageCount={pageCount}
                        setPage={setPage}
                      />
                    )}
                    <InquirySuggestionBanner />
                  </div>
                ) : (
                  <NoItemsBanner
                    title={t(noInquiriesBannerTrns.title)}
                    description={t(noInquiriesBannerTrns.description)}
                    href={PATHNAMES.CREATE_INQUIRY}
                    textButton={t(
                      noInquiriesBannerTrns.addInquiryWithFiltersButton
                    )}
                    onlyFor={ONLY_FOR.AGENT}
                  />
                )}
              </div>
            )}
          </div>
        </section>
      </PageWrapper>
    </MetadataWrapper>
  );
};

export default Inquiries;
